import { useEffect, useRef, useState } from 'react'
import type { DeliveryScheduleItemOut } from '../api/types'
import { isoDate } from '../lib/dates'

function addDays(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return isoDate(date)
}

/**
 * Pick a delivery date for the selected items — today to 90 days out, the same
 * window as the app — and, for any item with qty > 1, how many go on this run.
 * The remainder stays in the inbox.
 */
export function ScheduleDialog({
  title,
  confirmLabel,
  items,
  onCancel,
  onConfirm,
}: {
  title: string
  confirmLabel: string
  items: DeliveryScheduleItemOut[]
  onCancel: () => void
  onConfirm: (date: string, qtys: Record<string, number>) => Promise<void>
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const [date, setDate] = useState(addDays(1))
  const [qtys, setQtys] = useState<Record<string, number>>(() =>
    Object.fromEntries(items.map((item) => [item.id, item.qty ?? 1])),
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const multi = items.filter((item) => (item.qty ?? 1) > 1)
  const min = addDays(0)
  const max = addDays(90)

  useEffect(() => {
    ref.current?.showModal()
  }, [])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await onConfirm(date, qtys)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setBusy(false)
    }
  }

  return (
    <dialog ref={ref} className="dialog" onCancel={onCancel}>
      <form onSubmit={submit} className="stack">
        <h2>{title}</h2>
        <div className="field">
          <label htmlFor="schedule-date">Delivery date</label>
          <input
            id="schedule-date"
            type="date"
            required
            min={min}
            max={max}
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </div>

        {multi.length ? (
          <div className="stack" style={{ gap: 8 }}>
            <p className="hint">
              Lower a quantity to split the delivery — the rest stays in the inbox for later.
            </p>
            {multi.map((item) => (
              <div key={item.id} className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <span>
                  {item.item_name}
                  <span className="cell-sub"> · {item.invoice_reference}</span>
                </span>
                <span className="row" style={{ alignItems: 'center', gap: 6 }}>
                  <input
                    type="number"
                    aria-label={`Quantity of ${item.item_name}`}
                    min={1}
                    max={item.qty ?? 1}
                    value={qtys[item.id]}
                    onChange={(event) =>
                      setQtys({
                        ...qtys,
                        [item.id]: Math.min(item.qty ?? 1, Math.max(1, Number(event.target.value) || 1)),
                      })
                    }
                    style={{ width: 72 }}
                  />
                  <span className="cell-sub">of {item.qty}</span>
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {error ? <div className="alert error">{error}</div> : null}

        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </form>
    </dialog>
  )
}
